import { z } from 'zod';
import { TimestampSchema } from '../common.js';
import { Iso639_1Schema } from '../entities/agent.js';
import { AgentKnowledgeConfigSchema, AgentKnowledgeRetrievalModeSchema, AgentKnowledgeSourceSchema, } from '../entities/agentKnowledge.js';
export { AgentKnowledgeConfigSchema };
export const AgentKnowledgeSourceSummarySchema = AgentKnowledgeSourceSchema.omit({
    body: true,
});
export const AgentKnowledgeListResponseSchema = z.object({
    config: AgentKnowledgeConfigSchema,
    sources: z.array(AgentKnowledgeSourceSummarySchema),
});
export const AgentKnowledgeConfigUpdateRequestSchema = z.object({
    retrievalMode: AgentKnowledgeRetrievalModeSchema,
    lookupPolicy: AgentKnowledgeConfigSchema.shape.lookupPolicy.optional(),
    searchLimit: AgentKnowledgeConfigSchema.shape.searchLimit.optional(),
    noMatchPolicy: AgentKnowledgeConfigSchema.shape.noMatchPolicy.optional(),
    languageFallback: AgentKnowledgeConfigSchema.shape.languageFallback.optional(),
});
export const AgentKnowledgeTextSourceCreateRequestSchema = z
    .object({
    title: z.string().min(1).max(160),
    lang: Iso639_1Schema,
    body: z.string().min(1).max(100_000),
    validFrom: AgentKnowledgeSourceSchema.shape.validFrom,
    validUntil: AgentKnowledgeSourceSchema.shape.validUntil,
})
    .refine((value) => !value.validFrom || !value.validUntil || value.validFrom < value.validUntil, {
    message: 'validUntil must be later than validFrom.',
    path: ['validUntil'],
});
export const AgentKnowledgeSourceResponseSchema = z.object({
    source: AgentKnowledgeSourceSummarySchema,
});
export const AgentKnowledgeSourceDetailResponseSchema = z.object({
    source: AgentKnowledgeSourceSchema,
});
/**
 * Replaces the editable fields for one source. Text sources require `body`;
 * uploaded file sources omit it because their content is replaced by a new
 * upload rather than edited in place.
 */
export const AgentKnowledgeSourceUpdateRequestSchema = z
    .object({
    title: z.string().min(1).max(160),
    lang: Iso639_1Schema,
    body: z.string().min(1).max(100_000).optional(),
    validFrom: AgentKnowledgeSourceSchema.shape.validFrom.nullable(),
    validUntil: AgentKnowledgeSourceSchema.shape.validUntil.nullable(),
})
    .refine((value) => !value.validFrom || !value.validUntil || value.validFrom < value.validUntil, {
    message: 'validUntil must be later than validFrom.',
    path: ['validUntil'],
});
export const AGENT_KNOWLEDGE_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const AgentKnowledgeFileMimeSchema = z.enum([
    'text/plain',
    'text/markdown',
    'text/csv',
    'application/pdf',
]);
export const AgentKnowledgeFileSourceUploadRequestSchema = z
    .object({
    title: z.string().min(1).max(160),
    lang: Iso639_1Schema,
    mime: AgentKnowledgeFileMimeSchema,
    bytes: z.number().int().positive().max(AGENT_KNOWLEDGE_FILE_MAX_BYTES),
    checksum: z.string().min(1).max(256).optional(),
    validFrom: AgentKnowledgeSourceSchema.shape.validFrom,
    validUntil: AgentKnowledgeSourceSchema.shape.validUntil,
})
    .refine((value) => !value.validFrom || !value.validUntil || value.validFrom < value.validUntil, {
    message: 'validUntil must be later than validFrom.',
    path: ['validUntil'],
});
export const AgentKnowledgeFileSourceUploadResponseSchema = z.object({
    source: AgentKnowledgeSourceSummarySchema,
    uploadUrl: z.string().url(),
    expiresAt: TimestampSchema,
});
export const AGENT_KNOWLEDGE_SEARCH_DEFAULT_LIMIT = 5;
export const AGENT_KNOWLEDGE_SEARCH_MAX_LIMIT = 8;
export const AgentKnowledgeSearchRequestSchema = z.object({
    query: z.string().min(1).max(1000),
    lang: Iso639_1Schema,
    limit: z.number().int().min(1).max(AGENT_KNOWLEDGE_SEARCH_MAX_LIMIT).optional(),
});
export const AgentKnowledgeSearchResultSchema = z.object({
    sourceId: z.string().uuid(),
    chunkId: z.string().min(1).max(120),
    title: z.string().min(1).max(160),
    lang: Iso639_1Schema,
    text: z.string().min(1).max(4_000),
    score: z.number().nonnegative(),
});
export const AgentKnowledgeSearchResponseSchema = z.object({
    results: z.array(AgentKnowledgeSearchResultSchema).max(AGENT_KNOWLEDGE_SEARCH_MAX_LIMIT),
});
//# sourceMappingURL=agentKnowledge.js.map